import { useState } from 'react';

import { useSnackbar } from 'notistack';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { RoomImageOrderOverrides } from './ImagesVideo/ImagesByRoom/utils';
import type { MediaImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  buildOfferImageOrderUpdatePayload,
  buildRoomImageOrderUpdatePayload,
  findPersistedRoomByKey,
  getSavedImageOrder,
  getSyncedImagePublicIds,
  isSameImageOrder,
  syncGalleryOrderAfterDelete,
} from './ImagesVideo/utils';

interface PersistChangesNotificationOptions {
  showSuccess?: boolean;
  showError?: boolean;
}

const DEFAULT_PERSIST_NOTIFICATIONS: PersistChangesNotificationOptions = {
  showSuccess: true,
  showError: true,
};

type SaveScope = 'all' | 'offer' | 'room';

interface UseOfferBuilderMediaOrderParams {
  offerId: string;
  offerImages: Array<App.OfferImage>;
  uploadedImages: Array<MediaImage>;
  mediaImages: Array<MediaImage>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  imagePublicIdReplacements: Record<string, string>;
  removedRoomImageIdsByRoomKey: Record<string, Array<string>>;
}

export interface OfferBuilderMediaOrderController {
  offerImageOrderOverrides: Array<string>;
  savedOfferGalleryOrder: Array<string>;
  savedRoomImageOrders: RoomImageOrderOverrides;
  roomImageOrderOverrides: RoomImageOrderOverrides;
  roomImageOrderResetKey: number;
  isSavingChanges: boolean;
  hasUnsavedOfferChanges: boolean;
  hasUnsavedRoomImageChanges: boolean;
  getSavedRoomImageOrder: (roomKey: string) => Array<string>;
  syncOrdersAfterDelete: (
    scope: 'offer' | 'room',
    roomKey: string | undefined,
    publicId: string,
    deletedImageWasPending: boolean,
  ) => void;
  reorderOfferGalleryImages: (orderedPublicIds: Array<string>) => void;
  reorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => void;
  saveRoomImageChanges: (roomKey?: string, options?: PersistChangesNotificationOptions) => Promise<void>;
  saveImagesVideoChanges: () => Promise<void>;
  discardChanges: (scope?: SaveScope) => void;
}

export default function useOfferBuilderMediaOrder({
  offerId,
  offerImages,
  uploadedImages,
  mediaImages,
  propertyRoomTypes,
  imagePublicIdReplacements,
  removedRoomImageIdsByRoomKey,
}: UseOfferBuilderMediaOrderParams): OfferBuilderMediaOrderController {
  const { enqueueSnackbar } = useSnackbar();
  const [roomImageOrderOverrides, setRoomImageOrderOverrides] = useState<RoomImageOrderOverrides>({});
  const [roomImageOrderResetKey, setRoomImageOrderResetKey] = useState(0);
  const [offerImageOrderOverrides, setOfferImageOrderOverrides] = useState<Array<string>>([]);
  const [savedOfferGalleryOrder, setSavedOfferGalleryOrder] = useState<Array<string>>([]);
  const [savedRoomImageOrders, setSavedRoomImageOrders] = useState<RoomImageOrderOverrides>({});
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const hasUnsavedOfferChanges = offerImageOrderOverrides.length > 0;
  const hasUnsavedRoomImageChanges = Object.keys(roomImageOrderOverrides).length > 0;

  const getSavedOfferImageOrder = () =>
    getSavedImageOrder({
      savedOrder: savedOfferGalleryOrder.length > 0 ? savedOfferGalleryOrder : undefined,
      images: mediaImages.filter((image) => image.sourceType === 'offer'),
      imagePublicIdReplacements,
    });

  const getSavedRoomImageOrder = (roomKey: string) =>
    getSavedImageOrder({
      savedOrder: savedRoomImageOrders[roomKey],
      images: mediaImages.filter((image) => image.sourceType === 'room' && image.sourceRoomId === roomKey),
      imagePublicIdReplacements,
    });

  const resolveOfferGalleryOrderAfterSave = () =>
    offerImageOrderOverrides.length > 0 ? offerImageOrderOverrides : getSavedOfferImageOrder();

  const resolveRoomImageOrderAfterSave = (roomKey: string) =>
    roomImageOrderOverrides[roomKey] ?? getSavedRoomImageOrder(roomKey);

  const reorderOfferGalleryImages = (orderedPublicIds: Array<string>) => {
    const savedOrder = getSavedOfferImageOrder();

    setOfferImageOrderOverrides(isSameImageOrder(savedOrder, orderedPublicIds) ? [] : orderedPublicIds);
  };

  const reorderRoomImages = (roomKey: string, orderedPublicIds: Array<string>) => {
    const savedOrder = getSavedRoomImageOrder(roomKey);
    const isOriginalOrder = isSameImageOrder(savedOrder, orderedPublicIds);

    setRoomImageOrderOverrides((currentOverrides) => {
      const nextOverrides = { ...currentOverrides };

      if (isOriginalOrder) {
        delete nextOverrides[roomKey];
      } else {
        nextOverrides[roomKey] = orderedPublicIds;
      }

      return nextOverrides;
    });
  };

  // Called when an image is deleted, so any pending/saved order for that gallery drops the removed id.
  const syncOrdersAfterDelete = (
    scope: 'offer' | 'room',
    roomKey: string | undefined,
    publicId: string,
    deletedImageWasPending: boolean,
  ) => {
    if (scope === 'offer') {
      const remainingOfferGalleryImages = mediaImages.filter(
        (mediaImage) => mediaImage.sourceType === 'offer' && mediaImage.publicId !== publicId,
      );
      const { nextSavedOrder, nextDirtyOrder } = syncGalleryOrderAfterDelete({
        remainingImages: remainingOfferGalleryImages,
        publicId,
        imagePublicIdReplacements,
        savedOrder: savedOfferGalleryOrder,
        dirtyOrder: offerImageOrderOverrides,
        deletedImageWasPending,
        hadSavedOrder: savedOfferGalleryOrder.length > 0,
      });

      setSavedOfferGalleryOrder(nextSavedOrder);
      setOfferImageOrderOverrides(nextDirtyOrder);
      return;
    }

    if (!roomKey) {
      return;
    }

    const remainingRoomImages = mediaImages.filter(
      (image) => image.sourceType === 'room' && image.sourceRoomId === roomKey && image.publicId !== publicId,
    );
    const { nextSavedOrder, nextDirtyOrder } = syncGalleryOrderAfterDelete({
      remainingImages: remainingRoomImages,
      publicId,
      imagePublicIdReplacements,
      savedOrder: savedRoomImageOrders[roomKey],
      dirtyOrder: roomImageOrderOverrides[roomKey],
      deletedImageWasPending,
      hadSavedOrder: (savedRoomImageOrders[roomKey]?.length ?? 0) > 0,
    });

    setSavedRoomImageOrders((currentOrders) => {
      const nextOrders = { ...currentOrders };

      if (nextSavedOrder.length > 0) {
        nextOrders[roomKey] = nextSavedOrder;
      } else {
        delete nextOrders[roomKey];
      }

      return nextOrders;
    });
    setRoomImageOrderOverrides((currentOverrides) => {
      const nextOverrides = { ...currentOverrides };

      if (nextDirtyOrder.length > 0) {
        nextOverrides[roomKey] = nextDirtyOrder;
      } else {
        delete nextOverrides[roomKey];
      }

      return nextOverrides;
    });
  };

  const saveOfferImageOrder = async () => {
    if (offerImageOrderOverrides.length === 0) {
      return;
    }

    const persistedOfferImages = uploadedImages.flatMap((image) => {
      if (image.sourceType !== 'offer' || !image.sourceImageId) {
        return [];
      }

      return [{ id_image: Number(image.sourceImageId), id_cloudinary_external: image.publicId } as App.OfferImage];
    });
    const updatePayload = buildOfferImageOrderUpdatePayload(
      offerImages.concat(persistedOfferImages),
      offerImageOrderOverrides,
    );

    if (updatePayload.length > 0) {
      await OffersService.updateImages(updatePayload, offerId);
    }
  };

  const saveRoomImageOrders = async (roomKey?: string) => {
    const roomKeys = roomKey ? [roomKey] : Object.keys(roomImageOrderOverrides);

    await Promise.all(
      roomKeys.map(async (roomKey) => {
        const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
        const propertyId = room?.property_id;
        const roomTypeId = room?.id;

        if (!propertyId || !roomTypeId) {
          throw new Error(`Unable to find room ${roomKey}`);
        }

        const persistedRoomImages = uploadedImages.flatMap((image) => {
          if (image.sourceType !== 'room' || image.sourceRoomId !== roomKey || !image.sourceImageId) {
            return [];
          }

          return [{ id: image.sourceImageId, id_cloudinary_external: image.publicId }];
        });
        const orderedPublicIds = roomImageOrderOverrides[roomKey] ?? getSavedRoomImageOrder(roomKey);
        const updatePayload = buildRoomImageOrderUpdatePayload(
          (room.images ?? []).concat(persistedRoomImages),
          orderedPublicIds,
        );

        if (roomImageOrderOverrides[roomKey] && updatePayload.length > 0) {
          await ReservationService.updateRoomTypeImages(updatePayload, propertyId, roomTypeId);
        }
      }),
    );
  };

  const persistChanges = async (
    scope: 'offer' | 'room',
    roomKey?: string,
    manageSavingState = true,
    notifications: PersistChangesNotificationOptions = DEFAULT_PERSIST_NOTIFICATIONS,
  ): Promise<boolean> => {
    const { showSuccess: showSuccessNotification = true, showError: showErrorNotification = true } = notifications;
    const shouldSaveOffer = scope === 'offer';
    const shouldSaveRoom = scope === 'room';
    const hasRoomChanges = roomKey ? !!roomImageOrderOverrides[roomKey] : hasUnsavedRoomImageChanges;

    if (!((shouldSaveOffer && hasUnsavedOfferChanges) || (shouldSaveRoom && hasRoomChanges))) {
      return false;
    }

    if (manageSavingState) {
      setIsSavingChanges(true);
    }

    try {
      if (shouldSaveOffer) {
        await saveOfferImageOrder();
      }

      if (shouldSaveRoom) {
        await saveRoomImageOrders(roomKey);
      }

      if (shouldSaveRoom) {
        setSavedRoomImageOrders((currentOrders) => {
          const nextOrders = { ...currentOrders };

          (roomKey ? [roomKey] : Object.keys(roomImageOrderOverrides)).forEach((persistedRoomKey) => {
            const roomGalleryImages = mediaImages.filter(
              (image) =>
                image.sourceType === 'room' &&
                image.sourceRoomId === persistedRoomKey &&
                !(removedRoomImageIdsByRoomKey[persistedRoomKey] ?? []).includes(image.publicId),
            );
            const syncedOrder = getSyncedImagePublicIds(
              resolveRoomImageOrderAfterSave(persistedRoomKey),
              roomGalleryImages,
              imagePublicIdReplacements,
            );

            if (syncedOrder.length > 0) {
              nextOrders[persistedRoomKey] = syncedOrder;
            } else {
              delete nextOrders[persistedRoomKey];
            }
          });

          return nextOrders;
        });
        setRoomImageOrderOverrides((currentOverrides) =>
          roomKey ? Object.fromEntries(Object.entries(currentOverrides).filter(([key]) => key !== roomKey)) : {},
        );
      }
      if (shouldSaveOffer) {
        setSavedOfferGalleryOrder(
          getSyncedImagePublicIds(
            resolveOfferGalleryOrderAfterSave(),
            mediaImages.filter((image) => image.sourceType === 'offer'),
            imagePublicIdReplacements,
          ),
        );
        setOfferImageOrderOverrides([]);
      }
      if (showSuccessNotification) {
        enqueueSnackbar(scope === 'room' ? 'Room images saved' : 'Offer images saved', { variant: 'success' });
      }

      return true;
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }
      if (showErrorNotification) {
        enqueueSnackbar(
          getErrorMessage(error, scope === 'room' ? 'Failed to save room images' : 'Failed to save offer images'),
          { variant: 'error' },
        );
      }
      throw error;
    } finally {
      if (manageSavingState) {
        setIsSavingChanges(false);
      }
    }
  };

  const saveRoomImageChanges = async (roomKey?: string, options?: PersistChangesNotificationOptions) => {
    await persistChanges('room', roomKey, true, { ...DEFAULT_PERSIST_NOTIFICATIONS, ...options });
  };

  const saveImagesVideoChanges = async () => {
    setIsSavingChanges(true);
    let savedOffer = false;

    try {
      savedOffer = await persistChanges('offer', undefined, false, { showSuccess: false, showError: false });
      const savedRoom = await persistChanges('room', undefined, false, { showSuccess: false, showError: false });

      if (savedOffer && savedRoom) {
        enqueueSnackbar('Images saved', { variant: 'success' });
      } else if (savedOffer) {
        enqueueSnackbar('Offer images saved', { variant: 'success' });
      } else if (savedRoom) {
        enqueueSnackbar('Room images saved', { variant: 'success' });
      }
    } catch (error) {
      if (savedOffer && hasUnsavedRoomImageChanges) {
        enqueueSnackbar('Offer images saved, but room images failed to save', { variant: 'warning' });
      } else {
        enqueueSnackbar(getErrorMessage(error, 'Failed to save images'), { variant: 'error' });
      }

      throw error;
    } finally {
      setIsSavingChanges(false);
    }
  };

  const discardChanges = (scope: SaveScope = 'all') => {
    const discardOffer = scope === 'all' || scope === 'offer';
    const discardRoom = scope === 'all' || scope === 'room';
    if (discardOffer) {
      setOfferImageOrderOverrides([]);
    }
    if (discardRoom) {
      setRoomImageOrderOverrides({});
      setRoomImageOrderResetKey((currentKey) => currentKey + 1);
    }
  };

  return {
    offerImageOrderOverrides,
    savedOfferGalleryOrder,
    savedRoomImageOrders,
    roomImageOrderOverrides,
    roomImageOrderResetKey,
    isSavingChanges,
    hasUnsavedOfferChanges,
    hasUnsavedRoomImageChanges,
    getSavedRoomImageOrder,
    syncOrdersAfterDelete,
    reorderOfferGalleryImages,
    reorderRoomImages,
    saveRoomImageChanges,
    saveImagesVideoChanges,
    discardChanges,
  };
}
